import { useEffect } from 'react';
import { selectUserInfo } from 'selectors/api.selector';
import { useSelector, useDispatch } from 'react-redux';
import {
  refreshCashApiOutage,
  retrieveBaseSesameCash,
} from 'actions/sesameCash.actions.js';
import { useCashPinToken } from 'hook/cashPinToken.hook';
import { useLogger } from 'hook/error.hook';
import { SESAME_CASH_BOOSTER_ENHANCED_1 } from 'types/sesameCash.constants';
import {
  determineIfShipmentNoticeShouldShow,
  isEnrolledNotFunded,
  isEnrolledAndFunded,
  isEnrolledInCash,
  canUserEnrollInCash,
} from 'helpers/sesameCash.helpers';
import { getDaysSince } from 'helpers/dateHelper';
import {
  getSessionCount,
  getSessionCountCashEnhanced,
  setSessionCountCashEnhanced,
} from 'helpers/localstorage';
import { useEventHandler } from 'hook/event.hook';
import { BANKING_VERTICAL } from 'types/mrph.vertical.constants';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';

const getSesameCashBankingInactive = userInfo => {
  if (userInfo.banking) {
    if (
      userInfo.banking.enrollStatus !== 'REGISTERED' &&
      userInfo.bankingWhiteListed
    ) {
      return true;
    }
  } else if (userInfo.bankingWhiteListed) {
    return true;
  }
  return false;
};

const getIsCashTokenTimeout = cashAccountError => {
  return !!(
    cashAccountError &&
    cashAccountError.errorCode &&
    cashAccountError.errorCode === '104'
  );
};

const determineEnhancedModuleVersion = (
  userInfo,
  enhanceCashModules,
  creditInfo,
  cashAccount,
) => {
  if (!userInfo) return null;
  const { userSegmentationNumber } = userInfo;

  if (!enhanceCashModules) return null;

  if (
    !isEnrolledAndFunded(userInfo, cashAccount) &&
    userSegmentationNumber !== 6
  ) {
    return SESAME_CASH_BOOSTER_ENHANCED_1;
  }
};

export const useSesameCash = (initUserInfo, config, condition) => {
  const dispatch = useDispatch();
  const {
    cashLoaded,
    cashLoading,
    cashAccount,
    cashAccountLoading,
    cashAccountError,
    allCardDetails,
    allCardDetailsError,
    creditInfo,
    userInfo,
  } = useSelector(state => ({
    ...selectUserInfo(state),
    cashLoading: state.sesameCash.cashLoading,
    cashLoaded: state.sesameCash.cashLoaded,
    cashAccount: state.sesameCash.cashAccount,
    cashAccountLoading: state.sesameCash.cashAccountLoading,
    cashAccountError: state.sesameCash.cashAccountError,
    allCardDetails: state.sesameCash.allCardDetails,
    allCardDetailsError: state.sesameCash.allCardDetailsError,
    creditInfo: state?.creditInfo?.creditInfo,
  }));
  const logger = useLogger({ name: 'sesameCashHelper.hook' });
  const eventHandler = useEventHandler();

  const showFundAccountView = cashAccount && !cashAccount.receivedFirstFund;
  const {
    cashPinToken,
    cashPinTokenLoading,
    cashPinTokenSuccess,
    cashPinTokenError,
  } = useCashPinToken();

  let enhancedCashSessionCount = getSessionCountCashEnhanced();

  const enhanceForExperiments = true;

  if (enhanceForExperiments && !enhancedCashSessionCount) {
    enhancedCashSessionCount = Number(getSessionCount() ?? 0);
    setSessionCountCashEnhanced(enhancedCashSessionCount);
  } else {
    enhancedCashSessionCount = Number(enhancedCashSessionCount ?? 0);
  }

  const enhanceCashModules =
    enhanceForExperiments &&
    !isEnrolledAndFunded(initUserInfo, cashAccount) &&
    initUserInfo?.userSegmentationNumber !== 6;

  const cashModuleVersion = determineEnhancedModuleVersion(
    initUserInfo,
    enhanceCashModules,
    creditInfo,
    cashAccount,
  );

  let sesameCashEnrolledView = isEnrolledInCash(userInfo),
    sesameCashUnenrolledView = canUserEnrollInCash(userInfo),
    errorDisableSesameCash = false;

  useEffect(() => {
    if (!cashLoaded && !cashLoading && initUserInfo) {
      dispatch(retrieveBaseSesameCash({ userInfo: initUserInfo })).catch(
        err => {
          logger.reportAPIError(err);
        },
      );
    }
    //eslint-disable-next-line
  }, [cashLoaded, cashLoading, initUserInfo]);

  const onClickRefreshCashApiOutage = () => {
    eventHandler(CLICK, {
      'Click Type': 'Refresh',
      Vertical: BANKING_VERTICAL,
    });
    dispatch(refreshCashApiOutage());
  };

  const shouldShowShippingInfo = determineIfShipmentNoticeShouldShow(
    cashAccount,
    allCardDetails,
  );

  const cashPinTokenErrorCashEnrolled =
    cashPinTokenError && isEnrolledInCash(initUserInfo);

  const isCashApiOutage =
    cashAccountError !== null ||
    allCardDetailsError !== null ||
    cashPinTokenErrorCashEnrolled;
  let loading = false;

  if (cashPinTokenError) {
    loading = false;
  } else if (isEnrolledInCash(initUserInfo)) {
    loading = !(cashAccount || cashAccountError);
  }

  return {
    loading,
    errorDisableSesameCash,
    sesameCashEnrolledView,
    sesameCashUnenrolledView,
    sesameCashBalance: cashAccount ? cashAccount.balance : 0.0,
    sesameCashAccount: cashAccount,
    cashAccountError,
    cashAccountLoading,
    isCashTokenTimeout: getIsCashTokenTimeout(cashAccountError),
    cashPinToken,
    cashPinTokenLoading,
    cashPinTokenSuccess,
    cashPinTokenError,
    getSesameCashBankingInactive,
    showFundAccountView,
    enhanceCashModules,
    cashModuleVersion,
    shouldShowShippingInfo,
    showWebBoosterSignup:
      isEnrolledNotFunded(initUserInfo, cashAccount) &&
      initUserInfo?.userSegmentationNumber !== 6 &&
      getDaysSince(initUserInfo?.createdDate) >= 8,
    showBoosterEnhancement:
      initUserInfo?.userSegmentationNumber >= 0 &&
      cashAccount?.receivedFirstFund,
    allCardDetails,
    isCashApiOutage,
    onClickRefreshCashApiOutage,
    isUnFunded: isEnrolledNotFunded(initUserInfo, cashAccount),
  };
};
